import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useNavigate } from 'Pods/Shared/ContextBasedRouter'
import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { clearModelSettings } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { setActiveMobilePanel, decrementVariationsCount } from 'Pods/ThreeDStudio/Store/actions/ui'
import { MOBILE_PANEL } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

const showNotification = (message, type) => {
  if (typeof window !== 'undefined' && window.showNotification) {
    window.showNotification(message, { type, autohide: true })
  }
}

function useVariationActions(variations = EMPTY_ARRAY) {
  const t = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { postData, isLoading: isAssigning } = useApiPost()
  const { deleteData, isLoading: isDeleting } = useApiDelete()

  const refreshLists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['variations'] })
    queryClient.invalidateQueries({ queryKey: ['libraryItems'] })
  }, [queryClient])

  const openNextVariation = useCallback((deletedRunId) => {
    const openedRunId = threeDStudioStore.getState().ui?.openedModel?.id
    if (openedRunId == null || String(openedRunId) !== String(deletedRunId)) {
      return
    }

    const remaining = variations.filter(variation => String(variation.id) !== String(deletedRunId))
    if (remaining.length === 0) {
      clearModelSettings()
      // The enhance tab the variations panel lives in is now disabled (no model);
      // move mobile back to the generate panel so the user is not stranded there.
      setActiveMobilePanel(MOBILE_PANEL.GENERATE)
      navigate('/ai', { replace: true })
      return
    }

    const deletedIndex = variations.findIndex(variation => String(variation.id) === String(deletedRunId))
    const next = remaining[Math.min(deletedIndex, remaining.length - 1)]
    navigate(
      buildSharedRunPathname({ id: next.id, slug: next.slug, title: next.title ?? next.prompt }),
      { replace: true, state: { keepVariationsTab: true } },
    )
  }, [variations, navigate])

  const assignMain = useCallback((runId, itemName = '') => {
    if (runId == null) return

    postData({
      path: `/api/internal/generator/runs/${runId}/assign_main`,
      onSuccess: () => {
        refreshLists()
        const message = t('viewerSidebar.enhance.variations.notifications.setAsMainSuccess', { name: itemName })
        showNotification(message, 'success')
      },
      onError: () => showNotification(t('viewerSidebar.enhance.variations.notifications.setAsMainError'), 'error'),
    })
  }, [t, postData, refreshLists])

  const deleteVariation = useCallback((runId, itemName = '') => {
    if (runId == null) return

    deleteData({
      path: `/api/internal/generator/runs/${runId}`,
      onSuccess: () => {
        decrementVariationsCount()
        openNextVariation(runId)
        refreshLists()
        const message = t('viewerSidebar.enhance.variations.notifications.deleteSuccess', { name: itemName })
        showNotification(message, 'success')
      },
      onError: () => showNotification(t('viewerSidebar.enhance.variations.notifications.deleteError'), 'error'),
    })
  }, [t, deleteData, openNextVariation, refreshLists])

  return { assignMain, deleteVariation, isAssigning, isDeleting }
}

export default useVariationActions
